import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Play, Pause, Send, RefreshCw, Trash2, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const WpImportPanel = () => {
  const [file, setFile] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const fetchStatus = React.useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/admin/wp-import/status`, { credentials: 'include' });
      if (r.ok) setStatus(await r.json());
    } catch { /* silent */ }
  }, []);

  React.useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const upload = async () => {
    if (!file) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_BASE}/admin/wp-import/users`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
      setResult(await r.json());
      await fetchStatus();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="wp-import-panel">
      {status && (
        <div className="text-sm">
          <span className="text-muted-foreground">État actuel : </span>
          <strong>{status.wp_users_phpass}</strong> comptes phpass à upgrader,{' '}
          <strong>{status.wp_users_upgraded_bcrypt}</strong> déjà passés en bcrypt
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-2 items-start">
        <input
          type="file" accept=".json"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm" data-testid="wp-import-file-input"
        />
        <button
          onClick={upload} disabled={!file || busy}
          className="px-4 py-1.5 bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 rounded-none"
          data-testid="wp-import-upload-btn"
        >
          {busy ? 'Import en cours…' : 'Importer'}
        </button>
      </div>
      {result && (
        <div className="bg-green-50 border-l-4 border-green-500 p-3 text-sm text-green-800">
          ✅ Import terminé&nbsp;: inséré {result.inserted}, mis à jour {result.updated},
          préservé {result.preserved_native}, ignoré {result.skipped}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-3 text-sm text-red-800">{error}</div>
      )}
    </div>
  );
};

const StatBox = ({ label, value, color = 'text-gray-900', testid, onClick }) => (
  <div
    className={`bg-white border p-4 ${onClick ? 'cursor-pointer transition-all hover:shadow-md hover:border-blue-500' : ''}`}
    data-testid={testid}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
  >
    <div className="text-xs uppercase text-gray-500 tracking-wide flex items-center justify-between">
      <span>{label}</span>
      {onClick && <span className="text-blue-500 text-lg leading-none">→</span>}
    </div>
    <div className={`text-2xl font-semibold mt-1 ${color}`}>{value}</div>
  </div>
);

const Pill = ({ status }) => {
  const map = {
    idle:    { bg: 'bg-gray-200',  text: 'text-gray-700',  label: 'Inactif' },
    running: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours' },
    paused:  { bg: 'bg-yellow-100',text: 'text-yellow-800',label: 'En pause' },
  };
  const s = map[status] || map.idle;
  return (
    <span className={`px-2 py-1 text-xs font-semibold ${s.bg} ${s.text} rounded-none`} data-testid="campaign-status-pill">
      {s.label}
    </span>
  );
};

const WelcomeCampaignPanel = () => {
  const [data, setData] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [opens, setOpens] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [unsubs, setUnsubs] = useState([]);
  const [highlightedList, setHighlightedList] = useState(null);
  const opensRef = useRef(null);
  const clicksRef = useRef(null);
  const unsubsRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [testTo, setTestTo] = useState('');
  const [preview, setPreview] = useState(null);

  const scrollToList = useCallback((which) => {
    const ref = which === 'opens' ? opensRef : which === 'clicks' ? clicksRef : unsubsRef;
    const el = ref.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlightedList(which);
    // Auto-clear the highlight after 2 s so the ring doesn't stick forever.
    setTimeout(() => setHighlightedList((cur) => (cur === which ? null : cur)), 2000);
  }, []);

  // editable form fields
  const [form, setForm] = useState({
    daily_cap: 150,
    interval_seconds: 180,
    active_hours_start: 9,
    active_hours_end: 19,
    subject: '',
    html_template: '',
    from_email: 'noreply@puzzlevideo.fr',
    from_name: 'Puzzle Vidéo',
    reply_to: 'noreply@puzzlevideo.fr',
  });

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/welcome-campaign/status`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData(j);
      setForm((prev) => ({
        ...prev,
        daily_cap: j.config.daily_cap,
        interval_seconds: j.config.interval_seconds,
        active_hours_start: j.config.active_hours?.start ?? 9,
        active_hours_end: j.config.active_hours?.end ?? 19,
        subject: j.config.subject ?? '',
        html_template: j.config.html_template ?? '',
        from_email: j.config.from_email ?? 'noreply@puzzlevideo.fr',
        from_name: j.config.from_name ?? 'Puzzle Vidéo',
        reply_to: j.config.reply_to ?? 'noreply@puzzlevideo.fr',
      }));
      // Parallel fetch of monitoring KPIs + recent activity
      const [m, o, c, u] = await Promise.allSettled([
        fetch(`${API_BASE}/admin/welcome-campaign/monitoring`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API_BASE}/admin/welcome-campaign/opens?limit=50`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API_BASE}/admin/welcome-campaign/clicks?limit=50`, { credentials: 'include' }).then((r) => r.json()),
        fetch(`${API_BASE}/admin/welcome-campaign/unsubscribes?limit=100`, { credentials: 'include' }).then((r) => r.json()),
      ]);
      if (m.status === 'fulfilled') setMonitoring(m.value);
      if (o.status === 'fulfilled') setOpens(Array.isArray(o.value) ? o.value : []);
      if (c.status === 'fulfilled') setClicks(Array.isArray(c.value) ? c.value : []);
      if (u.status === 'fulfilled') setUnsubs(Array.isArray(u.value) ? u.value : []);
    } catch (e) {
      setMessage({ type: 'error', text: `Erreur de chargement: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 5000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const post = async (path, body) => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/admin/welcome-campaign${path}`, {
        method: body !== undefined ? 'POST' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`HTTP ${res.status}: ${t}`);
      }
      const j = await res.json();
      setMessage({ type: 'success', text: `OK: ${JSON.stringify(j)}` });
      await fetchStatus();
      return j;
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const updateConfig = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/admin/welcome-campaign/config`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          daily_cap: parseInt(form.daily_cap, 10),
          interval_seconds: parseInt(form.interval_seconds, 10),
          active_hours_start: parseInt(form.active_hours_start, 10),
          active_hours_end: parseInt(form.active_hours_end, 10),
          subject: form.subject,
          html_template: form.html_template,
          from_email: form.from_email,
          from_name: form.from_name,
          reply_to: form.reply_to,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      const j = await res.json();
      setMessage({ type: 'success', text: `Configuration mise à jour: ${j.updated.join(', ')}` });
      await fetchStatus();
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  const loadPreview = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/welcome-campaign/preview?first_name=Jean`, { credentials: 'include' });
      const j = await res.json();
      setPreview(j);
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    }
  };

  const sendTest = async () => {
    if (!testTo) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/admin/welcome-campaign/send-test`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: testTo, first_name: 'Test' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text()}`);
      setMessage({ type: 'success', text: `Email de test envoyé à ${testTo}` });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-8 text-gray-500">Chargement…</div>;
  if (!data) return <div className="p-8 text-red-600">Impossible de charger la campagne.</div>;

  const cfg = data.config;
  const q = data.queue;
  const progress = q.total > 0 ? Math.round((q.sent / q.total) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="welcome-campaign-panel">
      {/* Header & status */}
      <Card className="rounded-none">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5" /> Campagne « Bienvenue sur le nouveau site »
              <Pill status={cfg.status} />
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Envoi automatisé aux clients migrés depuis l'ancien WordPress. {cfg.daily_cap}/jour, 1 toutes les {Math.round(cfg.interval_seconds/60)} min, {cfg.active_hours.start}h–{cfg.active_hours.end}h (Paris).
            </p>
          </div>
          <div className="flex gap-2">
            {cfg.status !== 'running' ? (
              <Button onClick={() => post('/start')} disabled={busy} className="rounded-none gap-2" data-testid="campaign-start-btn">
                <Play className="w-4 h-4" /> Démarrer
              </Button>
            ) : (
              <Button onClick={() => post('/pause')} disabled={busy} variant="outline" className="rounded-none gap-2" data-testid="campaign-pause-btn">
                <Pause className="w-4 h-4" /> Mettre en pause
              </Button>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatBox label="Total" value={q.total} testid="stat-total" />
            <StatBox label="En attente" value={q.pending} color="text-blue-700" testid="stat-pending" />
            <StatBox label="Envoyés" value={q.sent} color="text-green-700" testid="stat-sent" />
            <StatBox label="Erreurs" value={q.errors} color="text-red-700" testid="stat-errors" />
            <StatBox label="Aujourd'hui" value={`${cfg.today_sent_count}/${cfg.daily_cap}`} testid="stat-today" />
          </div>

          {/* Progress */}
          {q.total > 0 && (
            <div>
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>Progression</span><span>{progress}%</span>
              </div>
              <div className="h-2 bg-gray-200">
                <div className="h-2 bg-green-600 transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {/* Empty queue while running → very visible warning */}
          {cfg.status === 'running' && q.total === 0 && (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 text-sm text-amber-900 space-y-2" data-testid="empty-queue-warning">
              <div className="flex items-start gap-2 font-semibold">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                La campagne tourne mais la file est vide — aucun email ne sera envoyé.
              </div>
              <p className="text-xs text-amber-800 leading-relaxed">
                Cliquez sur <strong>« Remplir la file (depuis les clients migrés) »</strong> ci-dessous
                pour charger les destinataires importés depuis WordPress. La campagne enverra
                automatiquement dès que la file sera peuplée.
              </p>
              <Button
                variant="outline"
                className="rounded-none gap-2 border-amber-500 text-amber-900 hover:bg-amber-100"
                disabled={busy}
                onClick={() => post('/seed-queue')}
                data-testid="empty-queue-seed-btn"
              >
                <Mail className="w-4 h-4" /> Remplir la file maintenant
              </Button>
            </div>
          )}

          {cfg.last_error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-3 text-sm text-red-800">
              <AlertCircle className="inline w-4 h-4 mr-1" />
              Dernière erreur : <code className="text-xs">{cfg.last_error}</code>
            </div>
          )}

          {/* Queue actions */}
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <Button
              variant="outline" className="rounded-none gap-2" disabled={busy}
              onClick={() => post('/seed-queue')} data-testid="campaign-seed-btn"
            >
              <Mail className="w-4 h-4" /> Remplir la file (depuis les clients migrés)
            </Button>
            <Button
              variant="outline" className="rounded-none gap-2" disabled={busy || q.errors === 0}
              onClick={() => post('/retry-errors')} data-testid="campaign-retry-btn"
            >
              <RefreshCw className="w-4 h-4" /> Re-tenter les erreurs ({q.errors})
            </Button>
            <Button
              variant="outline" className="rounded-none gap-2 text-red-700" disabled={busy || q.total === 0}
              onClick={() => { if (window.confirm(`Vider la file de ${q.total} email(s) ?`)) post('/reset-queue'); }}
              data-testid="campaign-reset-btn"
            >
              <Trash2 className="w-4 h-4" /> Vider la file
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Monitoring KPIs */}
      <Card className="rounded-none" data-testid="campaign-monitoring">
        <CardHeader>
          <CardTitle className="text-lg">Suivi des envois — ouvertures & clics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox
              label="Ouvertures"
              value={`${monitoring?.opened ?? 0} (${monitoring?.rates?.open_rate ?? 0}%)`}
              color="text-blue-700" testid="stat-opens"
              onClick={() => scrollToList('opens')}
            />
            <StatBox
              label="Clics"
              value={`${monitoring?.clicked ?? 0} (${monitoring?.rates?.click_rate ?? 0}%)`}
              color="text-emerald-700" testid="stat-clicks"
              onClick={() => scrollToList('clicks')}
            />
            <StatBox
              label="Taux clic / ouverture"
              value={`${monitoring?.rates?.click_to_open_rate ?? 0}%`}
              testid="stat-ctor"
            />
            <StatBox
              label="Désinscriptions"
              value={monitoring?.unsubscribed_total ?? 0}
              color="text-red-700" testid="stat-unsubs"
              onClick={() => scrollToList('unsubs')}
            />
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            L&apos;ouverture est détectée quand le destinataire autorise l&apos;affichage des images distantes.
            Certains clients mail (Apple Mail Privacy Protection, Outlook cache) peuvent gonfler ce chiffre :
            le <strong>taux de clic</strong> reste l&apos;indicateur d&apos;engagement le plus fiable.
          </p>
        </CardContent>
      </Card>

      {/* Recent opens & clicks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card ref={opensRef} className={`rounded-none transition-all ${highlightedList === 'opens' ? 'ring-2 ring-blue-500 shadow-lg' : ''}`}>
          <CardHeader><CardTitle className="text-base">Clients ayant ouvert le mail</CardTitle></CardHeader>
          <CardContent className="max-h-80 overflow-y-auto text-sm" data-testid="opens-list">
            {opens.length === 0 ? (
              <p className="text-xs text-gray-500">Aucune ouverture pour le moment.</p>
            ) : (
              <table className="w-full">
                <thead className="text-xs text-gray-500 uppercase">
                  <tr><th className="text-left py-1">Email</th><th className="text-right py-1">Ouv.</th><th className="text-right py-1">Depuis</th></tr>
                </thead>
                <tbody>
                  {opens.map((o) => (
                    <tr key={o.email} className="border-t">
                      <td className="py-1 pr-2 truncate max-w-[220px]">{o.email}</td>
                      <td className="py-1 text-right">{o.open_count || 1}</td>
                      <td className="py-1 text-right text-xs text-gray-500">
                        {o.opened_at ? new Date(o.opened_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card ref={clicksRef} className={`rounded-none transition-all ${highlightedList === 'clicks' ? 'ring-2 ring-emerald-500 shadow-lg' : ''}`}>
          <CardHeader><CardTitle className="text-base">Clients ayant cliqué vers le site</CardTitle></CardHeader>
          <CardContent className="max-h-80 overflow-y-auto text-sm" data-testid="clicks-list">
            {clicks.length === 0 ? (
              <p className="text-xs text-gray-500">Aucun clic pour le moment.</p>
            ) : (
              <table className="w-full">
                <thead className="text-xs text-gray-500 uppercase">
                  <tr><th className="text-left py-1">Email</th><th className="text-right py-1">Clics</th><th className="text-right py-1">Dernier</th></tr>
                </thead>
                <tbody>
                  {clicks.map((c) => (
                    <tr key={c.email} className="border-t">
                      <td className="py-1 pr-2 truncate max-w-[220px]">{c.email}</td>
                      <td className="py-1 text-right">{c.click_count || 1}</td>
                      <td className="py-1 text-right text-xs text-gray-500">
                        {c.last_click_at ? new Date(c.last_click_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Unsubscribes */}
      {unsubs.length > 0 && (
        <Card ref={unsubsRef} className={`rounded-none transition-all ${highlightedList === 'unsubs' ? 'ring-2 ring-red-500 shadow-lg' : ''}`}>
          <CardHeader><CardTitle className="text-base">Désinscriptions</CardTitle></CardHeader>
          <CardContent className="max-h-64 overflow-y-auto text-sm" data-testid="unsubs-list">
            <table className="w-full">
              <thead className="text-xs text-gray-500 uppercase">
                <tr><th className="text-left py-1">Email</th><th className="text-left py-1">Raison</th><th className="text-right py-1">Date</th></tr>
              </thead>
              <tbody>
                {unsubs.map((u) => (
                  <tr key={u.email} className="border-t">
                    <td className="py-1 pr-2 truncate max-w-[220px]">{u.email}</td>
                    <td className="py-1 pr-2 text-xs text-gray-600 truncate max-w-[300px]">{u.reason || '—'}</td>
                    <td className="py-1 text-right text-xs text-gray-500">
                      {u.created_at ? new Date(u.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Send test */}
      <Card className="rounded-none">
        <CardHeader><CardTitle className="text-lg">Importer les utilisateurs WordPress</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Charge le fichier <code>wp_users_dump.json</code> pour créer/mettre à jour les comptes
            des clients migrés depuis l'ancien site WordPress. Idempotent : tu peux relancer
            sans risque, les mots de passe déjà passés en bcrypt ne sont jamais ré-écrasés.
          </p>
          <WpImportPanel />
        </CardContent>
      </Card>

      {/* Send test */}
      <Card className="rounded-none">
        <CardHeader><CardTitle className="text-lg">Envoyer un email de test</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-2 items-start">
            <Input
              type="email" placeholder="votre@email.fr" className="rounded-none md:w-80"
              value={testTo} onChange={(e) => setTestTo(e.target.value)}
              data-testid="campaign-test-email-input"
            />
            <Button onClick={sendTest} disabled={busy || !testTo} className="rounded-none gap-2" data-testid="campaign-test-send-btn">
              <Send className="w-4 h-4" /> Envoyer le test
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recipients review — export */}
      <Card className="rounded-none">
        <CardHeader>
          <CardTitle className="text-lg">Revue des destinataires</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Vérifiez comment chaque destinataire sera salué avant de lancer la
            campagne : <em>« Bonjour Prénom, »</em> si un prénom exploitable est
            détecté, sinon <em>« Bonjour l'équipe Nom-de-société, »</em>.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(`${API_BASE}/admin/welcome-campaign/recipients-review.csv`, '_blank')}
              className="rounded-none gap-2"
              data-testid="campaign-recipients-csv-btn"
            >
              <Mail className="w-4 h-4" /> Télécharger le CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(`${API_BASE}/admin/welcome-campaign/recipients-review`, '_blank')}
              className="rounded-none gap-2"
              data-testid="campaign-recipients-json-btn"
            >
              Voir en JSON
            </Button>
            <label className="inline-flex items-center gap-2 border border-input px-3 py-2 text-sm font-medium cursor-pointer hover:bg-accent transition-colors rounded-none" data-testid="campaign-import-greetings-label">
              <Mail className="w-4 h-4" />
              <span>Importer la liste revue (CSV)</span>
              <input
                type="file"
                accept=".csv"
                className="sr-only"
                data-testid="campaign-import-greetings-input"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; e.target.value = '';
                  if (!f) return;
                  setBusy(true);
                  try {
                    const fd = new FormData(); fd.append('file', f);
                    const res = await fetch(`${API_BASE}/admin/welcome-campaign/greeting-overrides/import`, {
                      method: 'POST', credentials: 'include', body: fd,
                    });
                    const j = await res.json();
                    if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
                    setMessage({ type: 'success', text: `Import OK : ${j.upserted} salutations enregistrées (${j.skipped} lignes ignorées).` });
                  } catch (err) {
                    setMessage({ type: 'error', text: `Import KO : ${err.message}` });
                  } finally { setBusy(false); }
                }}
              />
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Le CSV importé est utilisé <strong>en priorité</strong> sur l&apos;heuristique automatique.
            Format attendu : colonnes <code>email</code> et <code>greeting</code> (le format exporté par
            « Télécharger le CSV » ci-dessus est directement ré-importable).
          </p>
        </CardContent>
      </Card>

      {/* Config form */}
      <Card className="rounded-none">
        <CardHeader><CardTitle className="text-lg">Paramètres d'envoi</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Cap quotidien</Label>
              <Input type="number" min="1" value={form.daily_cap}
                onChange={(e) => setForm({ ...form, daily_cap: e.target.value })}
                className="rounded-none" data-testid="campaign-cfg-cap" />
            </div>
            <div>
              <Label>Intervalle (secondes)</Label>
              <Input type="number" min="30" value={form.interval_seconds}
                onChange={(e) => setForm({ ...form, interval_seconds: e.target.value })}
                className="rounded-none" data-testid="campaign-cfg-interval" />
            </div>
            <div>
              <Label>Heure de début (Paris)</Label>
              <Input type="number" min="0" max="23" value={form.active_hours_start}
                onChange={(e) => setForm({ ...form, active_hours_start: e.target.value })}
                className="rounded-none" />
            </div>
            <div>
              <Label>Heure de fin (Paris)</Label>
              <Input type="number" min="1" max="24" value={form.active_hours_end}
                onChange={(e) => setForm({ ...form, active_hours_end: e.target.value })}
                className="rounded-none" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Expéditeur (email)</Label>
              <Input value={form.from_email}
                onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                className="rounded-none" data-testid="campaign-cfg-from" />
            </div>
            <div>
              <Label>Nom expéditeur</Label>
              <Input value={form.from_name}
                onChange={(e) => setForm({ ...form, from_name: e.target.value })}
                className="rounded-none" />
            </div>
            <div>
              <Label>Reply-To</Label>
              <Input value={form.reply_to}
                onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
                className="rounded-none" />
            </div>
          </div>

          <div>
            <Label>Sujet</Label>
            <Input value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="rounded-none" data-testid="campaign-cfg-subject" />
          </div>

          <div>
            <Label>Modèle HTML (variables disponibles : <code>{'{first_name}'}</code>, <code>{'{last_name}'}</code>, <code>{'{email}'}</code>, <code>{'{frontend_url}'}</code>)</Label>
            <Textarea
              value={form.html_template}
              onChange={(e) => setForm({ ...form, html_template: e.target.value })}
              rows={14} className="rounded-none font-mono text-xs"
              data-testid="campaign-cfg-html"
            />
          </div>

          <div className="flex gap-2">
            <Button onClick={updateConfig} disabled={busy} className="rounded-none gap-2" data-testid="campaign-cfg-save-btn">
              <CheckCircle2 className="w-4 h-4" /> Sauvegarder
            </Button>
            <Button onClick={loadPreview} variant="outline" className="rounded-none" data-testid="campaign-preview-btn">
              Voir l'aperçu
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {preview && (
        <Card className="rounded-none">
          <CardHeader><CardTitle className="text-lg">Aperçu (sujet : {preview.subject})</CardTitle></CardHeader>
          <CardContent>
            <iframe
              title="email-preview"
              srcDoc={preview.html}
              className="w-full h-[600px] border"
              data-testid="campaign-preview-iframe"
            />
          </CardContent>
        </Card>
      )}

      {/* Inline message */}
      {message && (
        <div
          className={`p-3 text-sm border-l-4 ${message.type === 'error' ? 'bg-red-50 border-red-500 text-red-800' : 'bg-green-50 border-green-500 text-green-800'}`}
          data-testid="campaign-inline-message"
        >
          {message.text}
        </div>
      )}
    </div>
  );
};

export default WelcomeCampaignPanel;
